import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button } from "@shopify/polaris";
import { toast } from "sonner";
import {
  createJobLeave,
  fetchJobLeavesByOnboarding,
} from "services/job-leave.service";
import { JobLeave } from "types/JobLeave";
import {
  ALL_JOB_LEAVE_STEPS,
  JOB_LEAVE_STEP_LABELS,
  getJobLeaveStatusLabel,
  getJobLeaveStatusTone,
  normalizeJobLeaveStatus,
} from "utils/jobLeaveStatus";
import JobLeaveViewContainer from "containers/job-leave-view-container/job-leave-view-container";

/**
 * Leave tab content for an onboarding record.
 */
function OnboardingJobLeaveSection({
  companyId,
  onboardingId,
  membershipDisabled = false,
  onLeavePresenceChange,
  onLeaveStatusChange,
}: {
  companyId: string;
  onboardingId: string;
  membershipDisabled?: boolean;
  onLeavePresenceChange?: (active: boolean) => void;
  onLeaveStatusChange?: (status: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [jobLeaves, setJobLeaves] = useState<JobLeave[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const notifyPresence = useCallback(
    (rows: JobLeave[]) => {
      const relevant = rows.some((r) => {
        const s = normalizeJobLeaveStatus(r.status);
        return s !== "cancelled";
      });
      onLeavePresenceChange?.(relevant);
    },
    [onLeavePresenceChange]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchJobLeavesByOnboarding(companyId, onboardingId);
      setJobLeaves(rows);
      notifyPresence(rows);
      const active = rows.find((r) => {
        const s = normalizeJobLeaveStatus(r.status);
        return s !== "completed" && s !== "cancelled";
      });
      const completed = rows.find(
        (r) => normalizeJobLeaveStatus(r.status) === "completed"
      );
      const preferred = active || completed || null;
      setSelectedId(preferred?.id || null);
      onLeaveStatusChange?.(
        preferred ? normalizeJobLeaveStatus(preferred.status) : null
      );
    } catch {
      toast.error(t("error"));
      setJobLeaves([]);
      setSelectedId(null);
      onLeavePresenceChange?.(false);
      onLeaveStatusChange?.(null);
    } finally {
      setLoading(false);
    }
  }, [
    companyId,
    onboardingId,
    t,
    notifyPresence,
    onLeavePresenceChange,
    onLeaveStatusChange,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const activeLeave = jobLeaves.find((r) => {
    const s = normalizeJobLeaveStatus(r.status);
    return s !== "completed" && s !== "cancelled";
  });

  const hasCompletedLeave = jobLeaves.some(
    (r) => normalizeJobLeaveStatus(r.status) === "completed"
  );

  const canStart =
    !membershipDisabled &&
    !hasCompletedLeave &&
    !activeLeave &&
    !loading &&
    !starting;

  const handleStart = async () => {
    if (!canStart) return;
    setStarting(true);
    try {
      const created = await createJobLeave(companyId, onboardingId);
      toast.success(t("job_leave_created"));
      await load();
      setSelectedId(created.id);
      onLeavePresenceChange?.(true);
      onLeaveStatusChange?.(normalizeJobLeaveStatus(created.status));
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        t("job_leave_create_failed", {
          defaultValue: "Could not start leave",
        });
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (!selectedId) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
        <div className="max-w-lg mx-auto text-center flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("employee_leave_from_company")}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {membershipDisabled
                ? t("employment_leave_membership_disabled")
                : t("employee_leave_from_company_hint")}
            </p>
          </div>

          <div className="w-full flex items-center justify-center gap-2 flex-wrap pt-1">
            {ALL_JOB_LEAVE_STEPS.map((step, idx) => (
              <div
                key={step}
                className="px-3 py-1.5 rounded-lg min-w-[80px] text-center bg-gray-100 text-gray-600"
              >
                <p className="text-sm">
                  {`${idx + 1}. ${
                    JOB_LEAVE_STEP_LABELS[step]?.[lang] ||
                    JOB_LEAVE_STEP_LABELS[step].en
                  }`}
                </p>
              </div>
            ))}
          </div>

          {canStart && (
            <Button variant="primary" onClick={handleStart} loading={starting}>
              {t("employee_leave_from_company")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {jobLeaves.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {jobLeaves.map((jl) => {
            const status = normalizeJobLeaveStatus(jl.status);
            const tone = getJobLeaveStatusTone(status);
            const selectedRow = jl.id === selectedId;
            return (
              <button
                key={jl.id}
                type="button"
                onClick={() => {
                  setSelectedId(jl.id);
                  onLeaveStatusChange?.(status);
                }}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                  selectedRow
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <Badge tone={tone}>
                  {getJobLeaveStatusLabel(status, t, lang)}
                </Badge>
              </button>
            );
          })}
        </div>
      )}

      {selectedId && (
        <JobLeaveViewContainer
          companyId={companyId}
          jobLeaveId={selectedId}
          embedded
          hideStepBar
          onProcessEnded={async () => {
            await load();
          }}
          onStatusChange={(status) => onLeaveStatusChange?.(status)}
        />
      )}
    </div>
  );
}

export default OnboardingJobLeaveSection;
